import { useTicketsStore } from '@/components/editor/TicketPlugin/TicketPlugin';
import {
  canViewInLightbox,
  Lightbox,
  LightboxRef,
} from '@/components/ui/Lightbox';
import {
  MessageComposer,
  Timeline as TimelineShell,
  TimelineTruncated,
} from '@/components/Timeline';
import downloadAttachment from '@/features/attachments/dowloadAttachment';
import { useCurrentUser, useUserMap } from '@/features/users/users.hooks';
import {
  AddAttachmentPayload,
  processTimelineEvents,
  TicketEvent,
  TicketWithEvents,
} from '@certhon/models';
import { useCallback, useMemo, useRef, useState } from 'react';
import { EventItem } from './EventItem';
import {
  createSyntheticTicketReferenceEvents,
  groupEventsByUserAndDay,
  groupHiddenEvents,
  isHistoryTruncated,
} from './timeline-utils';
import { useTicketMethods } from '@/features/tickets/components/Timeline/useTicketMethods';
import { HiddenEventsGroup } from './HiddenEventsGroup';

interface TimelineProps {
  events: TicketEvent[];
  ticketId: string;
  currentUserId?: number;
  referencedTickets: TicketWithEvents[];
}

// TODO: Allow editing an existing message
export function Timeline({
  events,
  ticketId,
  referencedTickets,
}: TimelineProps) {
  const user = useCurrentUser()!;
  const lightboxRef = useRef<LightboxRef>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  // Create user map
  const userMap = useUserMap();
  const ticketStore = useTicketsStore();

  const {
    postTicketEvents: mutation,
    deleteEvent,
    updateMessage,
    addMessage,
    addAttachments,
    addLocation,
  } = useTicketMethods(userMap, ticketStore, user, ticketId, events);

  const timelineItems = useMemo(() => {
    // fixme: refactor as functional pipe for readability
    const processedEvents = processTimelineEvents(events);
    const referenceEvents = createSyntheticTicketReferenceEvents(
      referencedTickets,
      ticketId,
    );

    const combined = [...processedEvents, ...referenceEvents];

    // Sort in reverse chronological order (newest first)
    // note: sorts combined in place
    const sorted = combined.sort(
      (a, b) => b.created.getTime() - a.created.getTime(),
    );

    const groupedEvents = groupEventsByUserAndDay(sorted);
    return groupHiddenEvents(groupedEvents);
  }, [events, referencedTickets, ticketId]);

  // Extract all attachment events the lightbox can show
  const lightboxAttachments = useMemo(() => {
    const rv: AddAttachmentPayload[] = [];
    timelineItems.forEach((item) => {
      const eventsToCheck = item.type === 'event' ? [item.event] : item.events;
      eventsToCheck.forEach((event) => {
        if (
          event.type === 'ADD_ATTACHMENT' &&
          canViewInLightbox(event.payload.contentType)
        ) {
          rv.push(event.payload);
        }
      });
    });
    return rv;
  }, [timelineItems]);

  const handleEditEvent = useCallback(
    (eventId: string) => {
      const event = events.find((e) => e.id === eventId);
      if (event && event.type === 'ADD_MESSAGE') {
        setEditingEventId(eventId);
      }
    },
    [events],
  );

  const handleCancelEdit = () => {
    setEditingEventId(null);
  };

  const handleEditLastMessage = useCallback(() => {
    // Find the last message event by the current user
    const userEvents = events
      .filter(
        (event) =>
          event.user_id === user.recnum && event.type === 'ADD_MESSAGE',
      )
      .sort((a, b) => b.created.getTime() - a.created.getTime());

    if (userEvents.length > 0) {
      const lastEvent = userEvents[0];
      handleEditEvent(lastEvent.id);
    }
  }, [events, handleEditEvent, user.recnum]);

  // Listen for Ctrl+Up to edit last message
  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     if (e.key === 'ArrowUp' && (e.ctrlKey || e.metaKey)) {
  //       e.preventDefault();
  //       handleEditLastMessage();
  //     }
  //   };

  //   document.addEventListener('keydown', handleKeyDown);
  //   return () => document.removeEventListener('keydown', handleKeyDown);
  // }, [handleEditLastMessage]);

  const handleDeleteEvent = (eventId: string) => {
    if (confirm('Are you sure you want to delete this event?')) {
      deleteEvent(eventId);
    }
  };

  const handleDownloadAttachment = (attachment: AddAttachmentPayload) => {
    void downloadAttachment(attachment);
  };

  const handleOpenInLightbox = (attachment: AddAttachmentPayload) => {
    lightboxRef.current?.open(attachment.checksum);
  };

  const handleUdateMessage = (eventId: string, message: string) => {
    updateMessage(eventId, message).then(() => setEditingEventId(null));
  };
  const isLoading = mutation.isPending;

  const timelineContent = timelineItems.map((item, index) => {
    // Items are newest-first; the chronologically previous (older) event sits
    // at index + 1. Passing the older neighbor keeps delta-mode semantics
    // ("time since previous event") correct in either display direction.
    const olderItem =
      index < timelineItems.length - 1 ? timelineItems[index + 1] : undefined;
    const previousTimestamp = olderItem
      ? olderItem.type === 'hidden-group'
        ? olderItem.events[0]?.created
        : olderItem.event.created
      : undefined;

    if (item.type === 'hidden-group') {
      return (
        <HiddenEventsGroup
          key={item.id}
          events={item.events}
          previousTimestamp={previousTimestamp}
          onEdit={handleEditEvent}
          editingEventId={editingEventId}
          onUpdateMessage={handleUdateMessage}
          onCancelEdit={handleCancelEdit}
          onDelete={handleDeleteEvent}
          onDownload={handleDownloadAttachment}
          onOpenInLightbox={handleOpenInLightbox}
        />
      );
    }

    // Determine if this is the start of a user+day group
    const event = item.event;

    return (
      <EventItem
        key={event.id}
        event={event}
        isGroupStart={event.isUserGroupStart ?? false}
        isLastEvent={index === timelineItems.length - 1}
        previousTimestamp={previousTimestamp}
        user={userMap.get(event.user_id)!}
        currentUserId={user.recnum}
        onEdit={() => handleEditEvent(event.id)}
        isEditing={editingEventId === event.id}
        onUpdateMessage={handleUdateMessage}
        onCancelEdit={handleCancelEdit}
        onDelete={() => handleDeleteEvent(event.id)}
        onDownload={handleDownloadAttachment}
        onOpenInLightbox={handleOpenInLightbox}
      />
    );
  });

  const composer = (
    <MessageComposer
      onEditLastMessage={handleEditLastMessage}
      onSendMessage={addMessage}
      onUploadAttachments={addAttachments}
      onAddLocation={addLocation}
      disabled={isLoading}
      placeholder="Add a message or upload files..."
    />
  );

  // Long-closed tickets sync without their events, so a short timeline means
  // "not downloaded" rather than "nothing happened". Inbound references are
  // affected too: they are read out of the REFERENCING ticket's events (see
  // createSyntheticTicketReferenceEvents), so they vanish when that ticket is
  // long closed even while this one is fully synced.
  const truncation = isHistoryTruncated(events)
    ? { show: true } // no message: TimelineTruncated's default wording fits
    : referencedTickets.some((ticket) => isHistoryTruncated(ticket.events))
      ? {
          show: true,
          message:
            'References from closed tickets have not been downloaded. Reconnect to load them.',
        }
      : { show: false };

  return (
    <div className="mt-8">
      <h2 className="text-foreground text-sm font-semibold">Timeline</h2>

      <TimelineShell composer={composer}>
        {timelineContent}
        {truncation.show && (
          <TimelineTruncated
            key="history-truncated"
            message={truncation.message}
          />
        )}
      </TimelineShell>

      <Lightbox ref={lightboxRef} attachments={lightboxAttachments} />
    </div>
  );
}
